import React from "react";
import { useTranslation } from "react-i18next";
import AppTable from "../../components/ui/AppTable";
import AppButton from "../../components/ui/AppButton";
import ListToolbar from "../../components/ui/ListToolbar";

const StatusBadge = ({ admin, t }) => {
  if (admin.banned) {
    return <span className="badge bg-danger">{t("staff:admins.table.statusBanned")}</span>;
  }
  if (!admin.active) {
    return <span className="badge bg-secondary">{t("staff:admins.table.statusInactive")}</span>;
  }
  return <span className="badge bg-success">{t("staff:admins.table.statusActive")}</span>;
};

const UMAuthorityAdminList = ({
  admins,
  loading,
  totalElements,
  currentPage,
  totalPages,
  selectedAdminId,
  superAdminCount,
  typeChangeLoadingId,
  onSelectAdmin,
  onAddAdmin,
  onChangeType,
  onPrevPage,
  onNextPage,
}) => {
  const { t } = useTranslation(["staff", "common"]);

  return (
    <>
      <ListToolbar
        title={t("staff:admins.title")}
        subtitle={t("staff:admins.subtitle", { count: totalElements })}
        addButton={{
          label: t("staff:admins.addAdmin"),
          onClick: onAddAdmin,
        }}
      />

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("staff:admins.loading")}</span>
              </div>
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-5 text-muted">{t("staff:admins.empty")}</div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-3">{t("staff:admins.table.name")}</th>
                  <th className="text-center">{t("staff:admins.table.status")}</th>
                  <th className="text-center">{t("staff:admins.table.type")}</th>
                  <th className="text-center pe-3">{t("staff:admins.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr
                    key={admin.id}
                    className={`border-bottom ${selectedAdminId === admin.id ? "table-active" : ""}`}
                    role="button"
                    onClick={() => onSelectAdmin(admin)}
                  >
                    <td className="ps-3">
                      <div className="fw-bold text-dark">{admin.fullName}</div>
                      <small className="text-muted">{admin.email}</small>
                      {admin.adminType !== "SUPER_ADMIN" && admin.allClubsAccess && (
                        <div>
                          <span className="badge bg-primary-subtle text-primary mt-1">
                            <i className="fas fa-globe me-1"></i>
                            {t("staff:clubAccess.allClubsAccess.badgeShort")}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      <StatusBadge admin={admin} t={t} />
                    </td>
                    <td className="text-center">
                      {admin.adminType === "SUPER_ADMIN" ? (
                        <span className="badge bg-danger">
                          <i className="fas fa-crown me-1"></i>
                          {t("staff:admins.type.SUPER_ADMIN")}
                        </span>
                      ) : admin.adminType === "SUB_ADMIN" ? (
                        <span className="badge bg-info">{t("staff:admins.type.SUB_ADMIN")}</span>
                      ) : (
                        <span className="badge bg-secondary">{t("staff:admins.type.unknown")}</span>
                      )}
                    </td>
                    <td className="text-center pe-3" onClick={(e) => e.stopPropagation()}>
                      <AppButton
                        variant="outline"
                        className="btn-sm"
                        disabled={
                          typeChangeLoadingId === admin.id ||
                          (admin.adminType === "SUPER_ADMIN" && superAdminCount <= 1)
                        }
                        title={
                          admin.adminType === "SUPER_ADMIN" && superAdminCount <= 1
                            ? t("staff:admins.lastSuperAdminTooltip")
                            : undefined
                        }
                        onClick={() => onChangeType(admin)}
                      >
                        {typeChangeLoadingId === admin.id ? (
                          <span className="spinner-border spinner-border-sm" role="status"></span>
                        ) : admin.adminType === "SUPER_ADMIN" ? (
                          t("staff:admins.makeSubAdmin")
                        ) : (
                          t("staff:admins.makeSuperAdmin")
                        )}
                      </AppButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <AppButton variant="outline" className="btn-sm" onClick={onPrevPage} disabled={currentPage === 0}>
            {t("common:buttons.previous")}
          </AppButton>
          <span className="text-muted small">
            {currentPage + 1} / {totalPages}
          </span>
          <AppButton
            variant="outline"
            className="btn-sm"
            onClick={onNextPage}
            disabled={currentPage >= totalPages - 1}
          >
            {t("common:buttons.next")}
          </AppButton>
        </div>
      )}
    </>
  );
};

export default UMAuthorityAdminList;
